import type { JSX } from "react";
import { useCallback, useEffect, useState } from "react";
import { feishuOAuthApi, type FeishuUserConnection } from "../lib/feishuOAuthApi";
import { FeishuPicker } from "./FeishuPicker";

/**
 * Per-user Feishu connection (personal-materials.md P4b). Two visual variants:
 *  - "row": compact connector row for the composer + menu (v3 entry);
 *  - "section": the fuller block (kept for the library manage view).
 * Tri-state either way: unconfigured (连接 → OAuth) / connected (name + masked
 * token + confirm-before-send toggle + 断开). The token never surfaces. The
 * OAuth callback bounces back with `?feishu=connected` (the app strips it and
 * reopens the surface); this component refetches on mount.
 */
export function FeishuConnectionSection({
  variant = "section",
  onPicked
}: {
  variant?: "section" | "row";
  /** Called after docs are picked from Feishu into 我的资料 (parent refreshes). */
  onPicked?: () => void;
}): JSX.Element {
  const [conn, setConn] = useState<FeishuUserConnection | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showPicker, setShowPicker] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setConn(await feishuOAuthApi.status());
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function onConnect(): Promise<void> {
    setErr("");
    setBusy(true);
    try {
      await feishuOAuthApi.begin(); // navigates away on success
    } catch {
      setErr("打不开飞书授权，稍后再试。");
      setBusy(false);
    }
  }

  async function onToggleConfirm(next: boolean): Promise<void> {
    setBusy(true);
    try {
      setConn(await feishuOAuthApi.setSendConfirm(next));
    } finally {
      setBusy(false);
    }
  }

  async function onDisconnect(): Promise<void> {
    setBusy(true);
    try {
      await feishuOAuthApi.disconnect();
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  const connected = conn?.connected === true;

  // Compact connector row for the + menu.
  if (variant === "row") {
    return (
      <div className="rm-feishu" data-testid="pkb-feishu">
        <div className="rm-row rm-static">
          <span className="rm-logo fs" aria-hidden="true">
            飞
          </span>
          <span className="rm-meta">
            <span className="rm-name">{connected ? `飞书 · ${conn?.displayName ?? "你的飞书"}` : "连接飞书"}</span>
            <span className="rm-sub">
              {loading ? "…" : connected ? "已连接 · 问到相关内容时自动查" : "用你的权限查你的文档"}
            </span>
          </span>
          {!loading &&
            (connected ? (
              <button className="rm-mini" type="button" disabled={busy} onClick={() => void onDisconnect()} data-testid="feishu-disconnect">
                断开
              </button>
            ) : (
              <button className="rm-add" type="button" disabled={busy} onClick={() => void onConnect()} data-testid="feishu-connect">
                连接
              </button>
            ))}
        </div>
        {connected && (
          <>
            <button
              type="button"
              className="rm-link fs-pick-link"
              onClick={() => setShowPicker(true)}
              data-testid="feishu-pick"
            >
              从飞书选文档加入 <span aria-hidden="true">→</span>
            </button>
            <label className="rm-confirm">
              <input
                type="checkbox"
                checked={conn?.sendConfirm !== false}
                disabled={busy}
                onChange={(e) => void onToggleConfirm(e.target.checked)}
                data-testid="feishu-send-confirm"
              />
              代发消息前先让我确认
            </label>
          </>
        )}
        {err !== "" && (
          <div className="pkb-err" role="alert">
            {err}
          </div>
        )}
        {showPicker && (
          <FeishuPicker
            onClose={() => setShowPicker(false)}
            onAdded={() => onPicked?.()}
          />
        )}
      </div>
    );
  }

  // Fuller section (library manage view).
  return (
    <div className="pkb-feishu" data-testid="pkb-feishu">
      <div className="pkb-feishu-head">连接飞书</div>
      {loading ? (
        <div className="pkb-note">加载中…</div>
      ) : !connected ? (
        <div className="pkb-feishu-row">
          <div className="pkb-feishu-meta">
            连接你的飞书，让小环用<b>你的</b>权限查文档、（确认后）代发消息。只属于你、随时可撤销。
          </div>
          <button className="pkb-upload" type="button" disabled={busy} onClick={() => void onConnect()} data-testid="feishu-connect">
            用飞书授权
          </button>
        </div>
      ) : (
        <>
          <div className="pkb-feishu-row">
            <div className="pkb-feishu-meta">
              <b>已连接 · {conn?.displayName ?? "你的飞书"}</b>
              <span className="pkb-feishu-mask"> {conn?.tokenMask}</span>
            </div>
            <button className="pkb-del" type="button" disabled={busy} onClick={() => void onDisconnect()} data-testid="feishu-disconnect">
              断开
            </button>
          </div>
          <button
            type="button"
            className="pkb-upload fs-pick-btn"
            onClick={() => setShowPicker(true)}
            data-testid="feishu-pick"
          >
            从飞书选文档加入我的资料
          </button>
          <label className="pkb-feishu-confirm">
            <input
              type="checkbox"
              checked={conn?.sendConfirm !== false}
              disabled={busy}
              onChange={(e) => void onToggleConfirm(e.target.checked)}
              data-testid="feishu-send-confirm"
            />
            代发消息前先让我确认
          </label>
        </>
      )}
      {err !== "" && (
        <div className="pkb-err" role="alert">
          {err}
        </div>
      )}
      {showPicker && (
        <FeishuPicker onClose={() => setShowPicker(false)} onAdded={() => onPicked?.()} />
      )}
    </div>
  );
}
